import { Box, Slider, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { HismithDeviceItem } from '../../../../hooks/useHismith';

interface DeviceIdleSettingsProps {
    device: HismithDeviceItem;
    isSaving: boolean;
    onSave: (idleSpeed: number, idleVibration: number) => void;
}

const marks = [
    { value: 0, label: '0%' },
    { value: 50, label: '50%' },
    { value: 100, label: '100%' },
];

const DeviceIdleSettings = ({ device, isSaving, onSave }: DeviceIdleSettingsProps) => {
    const [idleSpeed, setIdleSpeed] = useState<number>(device.idleSpeed);
    const [idleVibration, setIdleVibration] = useState<number>(device.idleVibration);

    useEffect(() => {
        setIdleSpeed(device.idleSpeed);
        setIdleVibration(device.idleVibration);
    }, [device.idleSpeed, device.idleVibration]);

    return (
        <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
                Idle speed is what the machine runs at between tips. It is applied when the device connects and restored after every tip.
                Leave it at 0% to stop between tips.
            </Typography>

            <Box sx={{ bgcolor: 'action.hover', px: 3, py: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <span>Idle speed</span>
                    <span>{idleSpeed}%</span>
                </Typography>
                <Slider
                    value={idleSpeed}
                    min={0}
                    max={100}
                    step={5}
                    marks={marks}
                    valueLabelDisplay="auto"
                    valueLabelFormat={v => `${v}%`}
                    disabled={isSaving}
                    onChange={(_, value) => setIdleSpeed(value as number)}
                    onChangeCommitted={(_, value) => onSave(value as number, idleVibration)}
                />
            </Box>

            {device.hasVibration && (
                <Box sx={{ bgcolor: 'action.hover', px: 3, py: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <span>Idle vibration</span>
                        <span>{idleVibration}%</span>
                    </Typography>
                    <Slider
                        value={idleVibration}
                        min={0}
                        max={100}
                        step={5}
                        marks={marks}
                        valueLabelDisplay="auto"
                        valueLabelFormat={v => `${v}%`}
                        disabled={isSaving}
                        onChange={(_, value) => setIdleVibration(value as number)}
                        onChangeCommitted={(_, value) => onSave(idleSpeed, value as number)}
                    />
                </Box>
            )}
        </Stack>
    );
};

export default DeviceIdleSettings;
